import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiResource } from "@/lib/api/types"
import type {
  DeclarationInput,
  HoldingFigures,
  Ownership,
  RecordHoldingInput,
} from "@/types/ownership"

type Answer = ApiResource<Ownership>

export const ownershipQueries = {
  firm: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/ownership`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<Ownership>>(
          `/firms/${firmId}/ownership`,
          { signal }
        )

        return data
      },
    }),
}

/** Every write answers with the whole structure, so it is taken as it comes. */
function useOwnershipWrite<TInput>(
  firmId: string,
  write: (input: TInput) => Promise<Answer>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: TInput) => (await write(input)).data,
    onSuccess: (ownership) => settle(queryClient, firmId, ownership),
  })
}

function settle(
  queryClient: QueryClient,
  firmId: string,
  ownership: Ownership
) {
  queryClient.setQueryData(apiKey(`/firms/${firmId}/ownership`), ownership)
}

export function useRecordHolding(firmId: string) {
  return useOwnershipWrite(
    firmId,
    async (input: RecordHoldingInput) =>
      await apiFetch<Answer>(`/firms/${firmId}/holdings`, {
        method: "POST",
        body: input,
      })
  )
}

export function useCorrectHolding(firmId: string) {
  return useOwnershipWrite(
    firmId,
    async ({ id, ...input }: HoldingFigures & { id: string }) =>
      await apiFetch<Answer>(`/holdings/${id}`, {
        method: "PATCH",
        body: input,
      })
  )
}

export function useRemoveHolding(firmId: string) {
  return useOwnershipWrite(
    firmId,
    async (id: string) =>
      await apiFetch<Answer>(`/holdings/${id}`, { method: "DELETE" })
  )
}

export function useDeclareOwnership(firmId: string) {
  return useOwnershipWrite(
    firmId,
    async (input: DeclarationInput) =>
      await apiFetch<Answer>(`/firms/${firmId}/ownership/declarations`, {
        method: "PUT",
        body: input,
      })
  )
}

/** Refused with a 409 naming the first blocking gap. */
export function useConfirmOwnership(firmId: string) {
  return useOwnershipWrite(
    firmId,
    async () =>
      await apiFetch<Answer>(`/firms/${firmId}/ownership/confirmations`, {
        method: "POST",
      })
  )
}
